import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Plus, Trash, Buildings, Storefront, Users, Briefcase } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_TYPES = [
  { value: 'company', label: 'Organizations', icon: Buildings, color: 'bg-blue-100 text-blue-700' },
  { value: 'vendor', label: 'Vendors', icon: Storefront, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'client', label: 'End Clients', icon: Users, color: 'bg-purple-100 text-purple-700' },
  { value: 'c2c_company', label: 'C2C Companies', icon: Briefcase, color: 'bg-amber-100 text-amber-700' },
];

const SOURCE_BADGES = {
  organization: { label: 'Organization / Entity', className: 'bg-blue-100 text-blue-700' },
  c2c: { label: 'C2C', className: 'bg-amber-100 text-amber-700' },
  both: { label: 'Org + C2C', className: 'bg-teal-100 text-teal-700' },
};

const EntityManagementPage = () => {
  const [activeType, setActiveType] = useState('company');
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/entities?entity_type=${activeType}`, { withCredentials: true });
      setEntities(res.data);
    } catch (error) {
      toast.error('Failed to load entities');
    } finally { setLoading(false); }
  }, [activeType]);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  const typeLabel = (type) => {
    if (type === 'company') return 'Organization';
    if (type === 'c2c_company') return 'C2C Company';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/admin/entities`, { type: activeType, name: newName.trim() }, { withCredentials: true });
      setNewName('');
      toast.success(`${typeLabel(activeType)} added`);
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    } finally { setAdding(false); }
  };

  const canDeleteEntity = (entity) => {
    if (activeType !== 'c2c_company') return true;
    // Merged list: only dedicated C2C entries (or both) can be removed from this tab
    return entity.source === 'c2c' || entity.source === 'both' || Boolean(entity.c2c_id);
  };

  const handleDelete = async (entity) => {
    if (activeType === 'c2c_company' && entity.source === 'organization' && !entity.c2c_id) {
      toast.error('This entry comes from Organizations / Entities. Remove it from the Organizations tab.');
      return;
    }
    const deleteId = entity.c2c_id || entity.id;
    if (!window.confirm(`Delete "${entity.name}"? This won't affect existing placements.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/entities/${deleteId}`, { withCredentials: true });
      toast.success('Deleted');
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const activeConfig = ENTITY_TYPES.find(t => t.value === activeType);
  const filteredEntities = entities.filter(e => e.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const isC2cTab = activeType === 'c2c_company';

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="entity-management-page">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900">Manage Organizations, Vendors, Clients & C2C</h1>
        <p className="text-sm text-slate-500 mt-1">Maintain dropdown lists used across the portal</p>
      </div>

      {/* Type Tabs */}
      <div className="flex flex-wrap gap-2">
        {ENTITY_TYPES.map(t => {
          const Icon = t.icon;
          return (
            <Button
              key={t.value}
              variant={activeType === t.value ? 'default' : 'outline'}
              onClick={() => { setActiveType(t.value); setSearchTerm(''); }}
              className="gap-2"
              data-testid={`tab-${t.value}`}
            >
              <Icon size={18} />
              {t.label}
            </Button>
          );
        })}
      </div>

      {isC2cTab && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900">
            C2C Companies list merges <strong>Organization / Entity</strong> names with dedicated
            <strong> C2C Companies</strong>. Both appear together in placement C2C dropdowns.
          </CardContent>
        </Card>
      )}

      {/* Add Form */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base font-['Outfit']">
            Add New {isC2cTab ? 'C2C Company' : activeConfig?.label?.replace(/s$/, '')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="flex gap-3 items-end">
            <div className="flex-1 space-y-2">
              <Label>Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={
                  activeType === 'company' ? 'Enter organization name...'
                    : activeType === 'c2c_company' ? 'Enter C2C company name...'
                      : `Enter ${activeType} name...`
                }
                data-testid="input-entity-name"
              />
            </div>
            <Button type="submit" disabled={adding || !newName.trim()} data-testid="btn-add-entity">
              <Plus size={16} className="mr-1" /> Add
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="flex items-center gap-4">
        <Input
          placeholder={`Search ${activeConfig?.label?.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
          data-testid="input-entity-search"
        />
        <Badge variant="secondary" className="text-sm">{filteredEntities.length} items</Badge>
      </div>

      {/* List */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filteredEntities.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No {activeConfig?.label?.toLowerCase()} found</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredEntities.map((entity) => {
                const sourceCfg = SOURCE_BADGES[entity.source] || null;
                return (
                  <div key={entity.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`entity-row-${entity.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge className={activeConfig?.color}>
                        {isC2cTab ? 'C2C' : activeType.charAt(0).toUpperCase()}
                      </Badge>
                      <span className="font-medium text-slate-800 truncate">{entity.name}</span>
                      {isC2cTab && sourceCfg && (
                        <Badge className={`${sourceCfg.className} text-[10px] shrink-0`}>{sourceCfg.label}</Badge>
                      )}
                    </div>
                    {canDeleteEntity(entity) ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                        onClick={() => handleDelete(entity)}
                        data-testid={`btn-delete-${entity.id}`}
                      >
                        <Trash size={16} />
                      </Button>
                    ) : (
                      <span className="text-[10px] text-slate-400 shrink-0 px-2">Managed in Organizations</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EntityManagementPage;
